import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Package, Truck, MapPin, Search, Download } from "lucide-react";
import { downloadRegistrationCard } from "@/lib/registrationCard";

const STATUS_META = {
  preparing: { label: "Preparing", color: "text-amber-300", bar: "w-1/3" },
  dispatched: { label: "Dispatched", color: "text-blue-300", bar: "w-2/3" },
  delivered: { label: "Delivered", color: "text-emerald-300", bar: "w-full" },
};

export default function Dashboard() {
  const { user, loading, login } = useAuth();
  const [orders, setOrders] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) return;
    if (user) api.get("/orders/my").then((r) => setOrders(r.data));
  }, [user, loading]);

  if (loading) return <div className="pt-32 text-center text-white/60">Loading…</div>;
  if (!user) {
    return (
      <div className="pt-32 max-w-md mx-auto text-center relative z-10">
        <h1 className="font-display text-3xl font-bold">Sign in to view your registrations</h1>
        <button className="btn-primary mt-6" onClick={login} data-testid="dash-login">Sign in with Google</button>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16 px-6 lg:px-10 relative z-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-gold">Your Registry</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Welcome, {user.name?.split(" ")[0]}
            </h1>
            <p className="text-white/60 mt-2">Track your commemorative registrations and courier status.</p>
          </div>
          <Link to="/" className="btn-ghost" data-testid="dash-new">Register a new plot</Link>
        </div>

        {orders.length === 0 ? (
          <div className="mt-16 glass rounded-3xl p-16 text-center">
            <MapPin className="w-8 h-8 text-white/40 mx-auto" />
            <div className="font-display text-2xl font-bold mt-4">No registrations yet</div>
            <p className="text-white/60 mt-2 text-sm">Start with a location on the Moon or Mars.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/explore/moon" className="btn-primary">Explore Moon</Link>
              <Link to="/explore/mars" className="btn-ghost">Explore Mars</Link>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid gap-4">
            {orders.map((o) => {
              const meta = STATUS_META[o.courier_status] || STATUS_META.preparing;
              return (
                <div key={o.id} className="glass rounded-2xl p-6 lg:p-8" data-testid={`order-${o.registration_number}`}>
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div>
                      <div className="overline text-gold">Registration No.</div>
                      <div className="font-mono-space text-xl text-white mt-1">{o.registration_number}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => downloadRegistrationCard(o)}
                        className="btn-ghost !py-2 !px-3 text-xs"
                        data-testid={`download-${o.registration_number}`}
                        title="Download registration number card"
                      >
                        <Download className="w-3.5 h-3.5" /> Card
                      </button>
                      <div className={`text-xs font-mono-space uppercase tracking-widest ${meta.color}`}>
                        {meta.label}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid sm:grid-cols-4 gap-6">
                    <Cell k="Planet" v={o.planet === "moon" ? "The Moon" : "Mars"} />
                    <Cell k="Location" v={o.marker_label} />
                    <Cell k="Size" v={`${o.acres} ${o.acres === 1 ? "Acre" : "Acres"}`} />
                    <Cell k="Purchased" v={new Date(o.purchase_date).toLocaleDateString()} />
                  </div>

                  <div className="mt-6">
                    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                      <div className={`h-full bg-[#D4AF37] transition-all duration-700 ${meta.bar}`} />
                    </div>
                    <div className="mt-4 flex flex-wrap justify-between gap-4 text-xs text-white/60">
                      <span className="flex items-center gap-2"><Package className="w-3.5 h-3.5" /> {o.courier_company || "Awaiting dispatch"}</span>
                      <span className="flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> {o.tracking_number || "Tracking N/A"}</span>
                      <span className="flex items-center gap-2"><Search className="w-3.5 h-3.5" /> <Link to={`/verify?rn=${o.registration_number}`} className="hover:text-white">Public verify</Link></span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ k, v }) {
  return (
    <div>
      <div className="overline">{k}</div>
      <div className="text-white mt-1 text-sm">{v}</div>
    </div>
  );
}
